import ChevronDown from "@shared/svg/chevron-down.svg?react";
import ConnectionLinkIcon from "@shared/svg/connection-link.svg?react";
import FacilityIcon from "@shared/svg/facility-list-item.svg?react";
import BodyText, { BODY_TEXT_SIZES } from "@shared/ui/BodyText";
import Headline, { HEADLINE_SIZES } from "@shared/ui/Headline";
import { CheckboxEmpty, CheckboxFilled } from "@shared/ui/Inputs";
import { useMemo, useState } from "react";

type ConnectionListItemPropsType = {
  connection: any;
  teamsComponent?: React.ReactNode;
  actionsComponent?: React.ReactNode;
  showExpandIcon?: boolean;
  isExpanded?: boolean;
  onToggleExpand?: () => void;
  subCompaniesLabel?: string;
  subCompanyLabel?: string;
  /**
   * When true, the leading facility icon is replaced by a selection checkbox on
   * hover (and permanently once selected or any item is selected). Opt-in so
   * non-selection usages keep their icon.
   */
  selectable?: boolean;
  /** Whether this card is currently selected (bulk selection mode). */
  isSelected?: boolean;
  /** Force the checkbox to stay visible (set once ≥1 item is selected). */
  anySelected?: boolean;
  /** Toggle this card's selection. */
  onToggleSelect?: () => void;
};

const ConnectionListItem = ({
  connection,
  teamsComponent = null,
  actionsComponent = null,
  showExpandIcon = false,
  isExpanded = false,
  onToggleExpand,
  subCompaniesLabel = "Sub Companies",
  subCompanyLabel = "Sub Company",
  selectable = false,
  isSelected = false,
  anySelected = false,
  onToggleSelect,
}: ConnectionListItemPropsType) => {
  const [isHovered, setIsHovered] = useState(false);
  const address = useMemo(() => {
    const location = connection?.customFields?.filter(
      (item) =>
        typeof item?.value === "object" &&
        Object.hasOwnProperty.call(item?.value || {}, "address"),
    );

    return location?.[0]?.value?.address || "";
  }, [connection?._id]);

  return (
    <div
      className="group px-lg mb-md rounded-lg border border-solid border-primary bg-primary hover:bg-accent flex items-center justify-between min-h-[4rem] py-md"
      onMouseEnter={() => setIsHovered(true)}
      onMouseLeave={() => setIsHovered(false)}
    >
      <div className="flex items-center gap-md flex-1 min-w-0">
        {showExpandIcon && (
          <button
            className="flex-shrink-0 w-xl h-xl flex items-center justify-center bg-transparent border-none cursor-pointer p-0"
            onClick={(e) => {
              e.stopPropagation();
              onToggleExpand?.();
            }}
            aria-label={isExpanded ? "Close" : "Open"}
          >
            <ChevronDown
              className={`w-lg h-lg transition-transform duration-200 stroke-secondary ${
                isExpanded ? "" : "-rotate-90"
              }`}
            />
          </button>
        )}
        {/* Leading icon — swapped for a selection checkbox (same 40px slot) on
            hover or once selected, so the title never shifts. */}
        {selectable && (isHovered || isSelected || anySelected) ? (
          <div
            className="flex items-center justify-center w-4xl h-4xl flex-shrink-0 cursor-pointer"
            onClick={(e) => {
              e.stopPropagation();
              onToggleSelect?.();
            }}
          >
            {isSelected ? (
              <CheckboxFilled width={18} height={18} className="flex-shrink-0" />
            ) : (
              // Empty checkbox has a transparent interior; add a white inner fill
              // so it reads as white on the hovered (bg-accent) row.
              <span
                className="relative flex items-center justify-center"
                style={{ width: 18, height: 18 }}
              >
                <span
                  className="absolute rounded-[4px] bg-white"
                  style={{ inset: 2 }}
                />
                <CheckboxEmpty
                  width={18}
                  height={18}
                  className="relative flex-shrink-0"
                />
              </span>
            )}
          </div>
        ) : (
          <div className="flex items-center justify-center w-4xl h-4xl flex-shrink-0 bg-purple-light rounded-full text-purple group-hover:bg-brand group-hover:text-inverse transition-all duration-200 relative">
            <FacilityIcon width={20} height={20} />
            {connection.linkedOrg && (
              <ConnectionLinkIcon className="w-md h-md absolute top-0 right-0" />
            )}
          </div>
        )}

        <div className="flex flex-col min-w-0 overflow-hidden mr-sm ">
          <Headline size={HEADLINE_SIZES.X_SMALL} className="w-full truncate">
            {connection.name}
          </Headline>
          <div className="flex items-center gap-xs mt-2xs overflow-hidden">
            {connection.facilityId && (
              <BodyText
                size={BODY_TEXT_SIZES.X_SMALL}
                color="text-secondary"
                className="truncate shrink-0 max-w-[120px]"
              >
                {connection.facilityId}
              </BodyText>
            )}
            {connection.totalChildrenCount > 0 && (
              <>
                {connection.facilityId && (
                  <BodyText
                    size={BODY_TEXT_SIZES.X_SMALL}
                    color="text-secondary"
                    className="flex-shrink-0"
                  >
                    •
                  </BodyText>
                )}
                <BodyText
                  size={BODY_TEXT_SIZES.X_SMALL}
                  color="text-secondary"
                  className="whitespace-nowrap flex-shrink-0"
                >
                  {connection.totalChildrenCount}{" "}
                  {connection.totalChildrenCount > 1
                    ? subCompaniesLabel
                    : subCompanyLabel}
                </BodyText>
              </>
            )}
            {connection.facilityId && address && (
              <BodyText
                size={BODY_TEXT_SIZES.X_SMALL}
                color="text-secondary"
                className="flex-shrink-0"
              >
                •
              </BodyText>
            )}
            {address && (
              <BodyText
                size={BODY_TEXT_SIZES.X_SMALL}
                color="text-secondary"
                className="truncate"
              >
                {address}
              </BodyText>
            )}
          </div>
        </div>
      </div>

      <div className="flex items-center gap-md flex-shrink-0">
        {teamsComponent && <div>{teamsComponent}</div>}
        {actionsComponent && (
          <div onClick={(e) => e.stopPropagation()}>{actionsComponent}</div>
        )}
      </div>
    </div>
  );
};

export default ConnectionListItem;
